import axios, { AxiosInstance } from 'axios'
import API_CONFIG from '../config/api.config'
import authRepository from './auth.repository'
import { Server, ServerGroupsResponse, ServerGroup, UpdateNicknameDTO } from '../types'

class ServersRepository {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      config => {
        const authHeaders = authRepository.getAuthHeader()
        if (authHeaders) {
          config.headers = { ...config.headers, ...authHeaders }
        }
        return config
      },
      error => Promise.reject(error)
    )

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          authRepository.clearAuthData()
          window.location.href = '/broker/login'
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Get all servers for the current partner
   * @returns Promise with array of servers
   */
  async getServers(): Promise<Server[]> {
    try {
      const response = await this.api.get<Server[]>(API_CONFIG.SERVERS.LIST)
      return response.data
    } catch (error) {
      console.error('Error fetching servers:', error)
      throw error
    }
  }

  /**
   * Get groups for a specific server
   * @param serverId - The server ID
   * @returns Promise with server groups response
   */
  async getServerGroups(serverId: number): Promise<ServerGroupsResponse> {
    try {
      const response = await this.api.get<ServerGroupsResponse>(
        API_CONFIG.SERVERS.GROUPS(serverId)
      )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 403) {
        console.warn('Server does not belong to partner:', serverId)
        // Return empty groups for servers that don't belong to partner
        return { groupsCount: 0, groups: [] }
      }
      console.error('Error fetching server groups:', error)
      throw error
    }
  }

  /**
   * Update the nickname of a server group
   * @param groupId - The group ID
   * @param nickname - The new nickname
   * @returns Promise with updated group
   */
  async updateGroupNickname(groupId: number, nickname: string): Promise<ServerGroup> {
    try {
      const payload: UpdateNicknameDTO = {
        groupNickname: nickname.trim()
      }
      const response = await this.api.put<ServerGroup>(
        API_CONFIG.SERVERS.UPDATE_NICKNAME(groupId),
        payload
      )
      return response.data
    } catch (error) {
      console.error('Error updating group nickname:', error)
      throw error
    }
  }
}

export default new ServersRepository()
